/**
 * AcquiredBooksPage - Detailed Acquired Book Performance by Office
 *
 * Displays acquired book performance metrics organized by:
 * 1. Office tabs (top level) - offices that have acquired book data
 * 2. Acquired template tabs (second level) - acquired books for selected office
 * 3. Metrics content - reuses AcquiredBookPerformanceSection patterns
 *
 * Features:
 * - URL state via query params for deep linking (?office=X&template=Y)
 * - Auto-select first office and template on mount
 * - Loading skeletons while data loads
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAcquiredBookOfficeMapping, OfficeWithAcquiredBooks } from '../../hooks/useAcquiredBookOfficeMapping';
import { useAcquiredBookMetrics, AcquiredBookMetrics } from '../../hooks/useAcquiredBookMetrics';
import { useLostLoans, LossReason, ALL_LOSS_REASONS, LOSS_REASON_LABELS } from '../../hooks/useLostLoans';
import { useLoanContacts } from '../../hooks/useLoanContacts';
import { useRefinanceData, RefinanceMatch } from '../../hooks/useRefinanceData';
import { formatCompactCurrency, formatCurrency } from '../../utils/formatters';
import { Skeleton } from '../../components/common/SkeletonLoader';
import { Spinner } from '../../components/common/Spinner';
import {
  BookOpen,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Share2,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  LineChart,
  XCircle,
  CheckCircle,
  ChevronRight,
  Library,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import RetentionTrendChart from '../../components/refinance/RetentionTrendChart';
import LostLoanCard from '../../components/refinance/LostLoanCard';
import ContactTimeline from '../../components/contacts/ContactTimeline';
import LossReasonStatCards from '../../components/refinance/LossReasonStatCards';
import BulkActionBar from '../../components/refinance/BulkActionBar';
import { Pagination } from '../../components/common/Pagination';

// Content tab type
type ContentTab = 'lost' | 'confirmed';

/**
 * Format YYYY-MM to "MMM YY" (e.g., "2023-04" → "Apr 23")
 * Note: Rolling 3-run month ranges are now pre-formatted by the backend
 */
const formatMonthShort = (yyyyMm: string): string => {
  if (!yyyyMm || yyyyMm.length < 7) return yyyyMm;
  // If already formatted (contains letters), return as-is
  if (/[a-zA-Z]/.test(yyyyMm)) return yyyyMm;
  const [year, month] = yyyyMm.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(month, 10) - 1;
  const shortYear = year.slice(2);
  return `${monthNames[monthIndex] || month} ${shortYear}`;
};

/** Compact currency from cents, the app-wide three-significant-figure rule. */
const formatCurrencyCompact = (cents: number): string => formatCompactCurrency(cents);

interface FileTemplate {
  id: string;
  templateName: string;
  isAcquiredLoanBook: boolean;
}

interface Office {
  id: string;
  name: string;
}

interface OutletContextType {
  userData: {
    uid?: string;
    companyId: string;
    role: string;
    displayName?: string;
    email?: string;
    commissionDataName?: string;
  };
  companyData: {
    id: string;
    name: string;
    enabledTemplateIds?: string[];
    primaryTemplateId?: string;
  };
  userVisibleOffices: Office[];
  userVisibleTemplates: any[];
  loanBooks?: ResolvedLoanBook[];
}

/**
 * Metric card component for summary stats
 */
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, subValue, icon, color, trend }) => (
  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
        <span className="text-gray-400 text-sm">{title}</span>
      </div>
      {trend && (
        <div className={`flex items-center ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
          {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
        </div>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value}</p>
      {subValue && <p className="text-sm text-gray-400 mt-1">{subValue}</p>}
    </div>
  </div>
);

/**
 * Skeleton for the metrics section
 */
const MetricsSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Summary Cards - Row 1 */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-start justify-between mb-3">
            <Skeleton width="60%" height="16px" />
            <Skeleton width="32px" height="32px" className="rounded-lg" />
          </div>
          <Skeleton width="80%" height="32px" className="mb-2" />
          <Skeleton width="50%" height="14px" />
        </div>
      ))}
    </div>
    {/* Chart skeleton */}
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <Skeleton width="200px" height="24px" className="mb-6" />
      <div className="h-64 flex items-end gap-2">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="flex-1" height={`${Math.random() * 60 + 40}%`} />
        ))}
      </div>
    </div>
  </div>
);

const AcquiredBooksPage: React.FC = () => {
  const context = useOutletContext<OutletContextType>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract context data
  const { userData, companyData, userVisibleOffices, userVisibleTemplates, loanBooks = [] } = context || {};
  const companyId = userData?.companyId || '';

  // URL state
  const selectedOfficeId = searchParams.get('office') || '';
  const selectedTemplateId = searchParams.get('template') || '';

  // Resolve loan book selection → query params (templateId + optional entityCode)
  const resolvedParams = useMemo(() =>
    resolveLoanBookSelection(selectedTemplateId, loanBooks),
    [selectedTemplateId, loanBooks]
  );
  const deepLinkLoanId = searchParams.get('loanId');

  // Deep-link state
  const deepLinkCardRef = useRef<HTMLDivElement | null>(null);
  const deepLinkProcessed = useRef(false);
  const [highlightedLoanId, setHighlightedLoanId] = useState<string | null>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  // Local state
  const [templates, setTemplates] = useState<FileTemplate[]>([]);
  const [primaryTemplate, setPrimaryTemplate] = useState<FileTemplate | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [chartView, setChartView] = useState<'combined' | 'simple'>('combined');

  // Tracks whether user has manually clicked a template tab (prevents auto-select race)
  const userSelectedTemplateRef = useRef(false);

  // Fetch office mapping
  const {
    offices: allOfficesWithAcquiredBooks,
    loading: loadingOffices,
    error: officeError,
    refresh: refreshOffices,
  } = useAcquiredBookOfficeMapping(companyId);

  // Filter offices to only those visible to current user (office_admin sees only assigned offices)
  const officesWithAcquiredBooks = useMemo(() => {
    if (!userVisibleOffices || userVisibleOffices.length === 0) return allOfficesWithAcquiredBooks;
    const visibleIds = new Set(userVisibleOffices.map(o => o.id));
    return allOfficesWithAcquiredBooks.filter(o => visibleIds.has(o.officeId));
  }, [allOfficesWithAcquiredBooks, userVisibleOffices]);

  // Fetch metrics
  const {
    metrics,
    isLoading: loadingMetrics,
    error: metricsError,
    fetchMetrics,
    clearMetrics,
  } = useAcquiredBookMetrics();

  // Content tab state (from URL or default to 'lost' to showcase the new feature)
  const contentTab = (searchParams.get('tab') as ContentTab) || 'lost';
  const [lossReasonFilter, setLossReasonFilter] = useState<LossReason | 'all'>('all');
  const [brokerFilter, setBrokerFilter] = useState<string>('all');
  const [categorizationFilter, setCategorizationFilter] = useState<'all' | 'needs_categorization' | 'completed'>('needs_categorization');

  // Broker list for filter dropdown (extracted from lost loans)
  const [brokerList, setBrokerList] = useState<{ id: string; name: string }[]>([]);

  // All active company brokers for contact "Made By" dropdown (primary book brokers make the calls)
  const [contactBrokerList, setContactBrokerList] = useState<{ id: string; name: string }[]>([]);

  // Pagination state for lost loans
  const [lostLoansPage, setLostLoansPage] = useState(1);
  const [lostLoansPageSize, setLostLoansPageSize] = useState(25);

  // Contact tracking state — expand/collapse affordance only; contact data is
  // sourced from the canonical loan-keyed store (useLoanContacts).
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Canonical loan-contacts store (dual-reads legacy collections + canonical,
  // merged + deduped by id). Writes land in companies/{c}/loan_contacts/{loanId}.
  const { contactsByLoan, mutateContact } = useLoanContacts(companyId);

  // Lost loans hook
  const {
    loans: lostLoans,
    metrics: lostLoansMetrics,
    total: lostLoansTotal,
    hasMore: hasMoreLostLoans,
    isLoading: loadingLostLoans,
    isSaving: savingLossReason,
    error: lostLoansError,
    fetchLostLoans,
    loadMore: loadMoreLostLoans,
    setLossReason,
    clearLoans: clearLostLoans,
    selectedLoanIds,
    toggleLoanSelection,
    selectAllLoans,
    clearSelection,
  } = useLostLoans();

  // Refinance data hook for confirmed refinances
  const {
    matches: refinanceMatches,
    isLoadingMatches: loadingRefinanceMatches,
    error: refinanceError,
    total: refinanceTotal,
    hasMore: hasMoreRefinanceMatches,
    fetchMatches: fetchRefinanceMatches,
    loadMore: loadMoreRefinanceMatches,
  } = useRefinanceData();

  // Group refinance matches by splitGroupId to avoid duplicates
  // Split loans have the same splitGroupId and should be shown as one grouped card
  const groupedRefinances = useMemo(() => {
    if (!refinanceMatches || refinanceMatches.length === 0) {
      return { groups: new Map<string, RefinanceMatch[]>(), singles: [] as RefinanceMatch[] };
    }

    const groups = new Map<string, RefinanceMatch[]>();
    const singles: RefinanceMatch[] = [];

    refinanceMatches.forEach(match => {
      if (match.splitGroupId) {
        const existing = groups.get(match.splitGroupId) || [];
        existing.push(match);
        groups.set(match.splitGroupId, existing);
      } else {
        singles.push(match);
      }
    });

    return { groups, singles };
  }, [refinanceMatches]);

  // Calculate unique refinance count (groups count as 1, singles count as 1 each)
  const uniqueRefinanceCount = groupedRefinances.groups.size + groupedRefinances.singles.length;

  // Helper to update URL params
  const updateUrlParams = useCallback((officeId: string, templateId: string, tab?: ContentTab) => {
    const params = new URLSearchParams();
    if (officeId) params.set('office', officeId);
    if (templateId) params.set('template', templateId);
    if (tab) params.set('tab', tab);
    else if (contentTab) params.set('tab', contentTab);
    setSearchParams(params, { replace: true });
  }, [contentTab, setSearchParams]);

  // Handle content tab change with scroll to content
  const handleTabChange = (tab: ContentTab) => {
    updateUrlParams(selectedOfficeId, selectedTemplateId, tab);
    // Scroll to the tab content after a brief delay for state update
    setTimeout(() => {
      const tabContent = document.getElementById('tab-content-section');
      if (tabContent) {
        tabContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Fetch lost loans when template changes, filter changes, or page changes
  useEffect(() => {
    if (contentTab === 'lost' && selectedTemplateId && companyId) {
      fetchLostLoans({
        companyId,
        acquiredTemplateId: selectedTemplateId,
        officeId: selectedOfficeId || undefined,
        brokerId: brokerFilter !== 'all' ? brokerFilter : undefined,
        categorizationStatus: categorizationFilter,
        lossReason: lossReasonFilter,
        entityCode: resolvedParams.entityCode,
        limit: lostLoansPageSize,
        offset: (lostLoansPage - 1) * lostLoansPageSize,
      });
    }
  }, [contentTab, selectedTemplateId, companyId, selectedOfficeId, brokerFilter, categorizationFilter, lossReasonFilter, lostLoansPage, lostLoansPageSize, fetchLostLoans]);

  // Reset page when filter changes
  useEffect(() => {
    setLostLoansPage(1);
  }, [lossReasonFilter, brokerFilter, categorizationFilter, selectedTemplateId, selectedOfficeId]);

  // Extract unique brokers from lost loans for filter dropdown
  useEffect(() => {
    if (lostLoans.length > 0) {
      const uniqueBrokers = new Map<string, string>();
      lostLoans.forEach(loan => {
        if (loan.brokerId && !uniqueBrokers.has(loan.brokerId)) {
          uniqueBrokers.set(loan.brokerId, loan.brokerDisplay || loan.brokerId);
        }
      });
      setBrokerList(Array.from(uniqueBrokers.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
    }
  }, [lostLoans]);

  // Fetch confirmed refinances when on confirmed tab
  useEffect(() => {
    if (contentTab === 'confirmed' && selectedTemplateId && companyId) {
      fetchRefinanceMatches(companyId, {
        status: 'confirmed',
        // Retained cards are refinances only; confirmed returning clients are
        // reported separately and must never render as retained money.
        relationship: 'refinance',
        templateId: selectedTemplateId,
        isAcquiredToPrimary: true,
        entityCode: resolvedParams.entityCode,
      });
    }
  }, [contentTab, selectedTemplateId, companyId, resolvedParams.entityCode, fetchRefinanceMatches]);

  // Handle setting loss reason (single or bulk)
  const handleSetLossReason = useCallback(async (
    loanIdOrIds: string | string[],
    reason: LossReason,
    notes?: string
  ): Promise<boolean> => {
    const loanIds = Array.isArray(loanIdOrIds) ? loanIdOrIds : [loanIdOrIds];
    return setLossReason({
      companyId,
      templateId: selectedTemplateId || undefined,
      loanIds,
      reason,
      notes,
    });
  }, [companyId, selectedTemplateId, setLossReason]);

  // Handle bulk set reason
  const handleBulkSetReason = useCallback(async (
    reason: LossReason,
    notes?: string
  ): Promise<boolean> => {
    return setLossReason({
      companyId,
      templateId: selectedTemplateId || undefined,
      loanIds: Array.from(selectedLoanIds),
      reason,
      notes,
    });
  }, [companyId, selectedTemplateId, selectedLoanIds, setLossReason]);

  // Fetch active company brokers for contact "Made By" dropdown
  // Broker role: only show the logged-in broker (skip Firestore read)
  // Other roles: fetch all active brokers from Firestore
  useEffect(() => {
    if (!userData?.companyId) return;

    if (userData?.role === 'broker' || userData?.role === 'broker_assistant') {
      const brokerName = userData.commissionDataName || userData.displayName || '';
      if (brokerName) {
        setContactBrokerList([{ id: 'self', name: brokerName }]);
      }
      return;
    }

    const brokersQuery = query(
      collection(db, `companies/${userData.companyId}/brokers`)
    );

    const unsubscribe = onSnapshot(
      brokersQuery,
      (snapshot) => {
        const brokers: { id: string; name: string }[] = [];
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.mergedInto) return;
          if ((data.status || 'active') !== 'active') return;
          if (data.currentName && typeof data.currentName === 'string') {
            brokers.push({ id: docSnap.id, name: data.currentName });
          }
        });
        brokers.sort((a, b) => a.name.localeCompare(b.name));
        setContactBrokerList(brokers);
      },
      (err) => {
        console.error('[AcquiredBooksPage] Error fetching brokers for contact dropdown:', err);
      }
    );

    return () => unsubscribe();
  }, [userData?.companyId, userData?.role, userData?.commissionDataName, userData?.displayName]);

  // Deep-link: expand card and scroll into view
  useEffect(() => {
    if (!deepLinkLoanId || loadingLostLoans || lostLoans.length === 0 || deepLinkProcessed.current) return;

    const found = lostLoans.find(l => l.loanId === deepLinkLoanId);
    if (!found) {
      // Remove loanId param but keep office/template params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('loanId');
      setSearchParams(newParams, { replace: true });
      deepLinkProcessed.current = true;
      return;
    }

    setExpandedCards(prev => new Set(prev).add(deepLinkLoanId));
    setHighlightedLoanId(deepLinkLoanId);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('loanId');
    setSearchParams(newParams, { replace: true });
    deepLinkProcessed.current = true;
    setScrollTrigger(prev => prev + 1);
  }, [deepLinkLoanId, loadingLostLoans, lostLoans, searchParams, setSearchParams]);

  // Deep-link: scroll to target card after render
  useEffect(() => {
    if (scrollTrigger === 0) return;
    const timer = setTimeout(() => {
      deepLinkCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollTrigger]);

  // Toggle card contact expansion
  const toggleCardExpansion = useCallback((loanId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  }, []);

  // Derive templates from outlet context (office-scoped for office_admin)
  useEffect(() => {
    if (!userVisibleTemplates || userVisibleTemplates.length === 0) {
      setLoadingTemplates(false);
      return;
    }

    const deriveTemplates = () => {
      setLoadingTemplates(true);
      try {
        const primaryTemplateId = companyData?.primaryTemplateId;
        const templateList: FileTemplate[] = [];
        let foundPrimary: FileTemplate | null = null;

        for (const t of userVisibleTemplates) {
          const template: FileTemplate = {
            id: t.id,
            templateName: t.templateName || t.name || 'Unknown',
            isAcquiredLoanBook: t.isAcquiredLoanBook || false,
          };
          templateList.push(template);

          if (t.id === primaryTemplateId) {
            foundPrimary = template;
          }
        }

        setTemplates(templateList);
        setPrimaryTemplate(foundPrimary);
      } finally {
        setLoadingTemplates(false);
      }
    };

    deriveTemplates();
  }, [userVisibleTemplates, companyData?.primaryTemplateId]);

  // Get acquired templates for the selected office
  const acquiredTemplatesForOffice = useMemo(() => {
    if (!selectedOfficeId) return [];

    const officeData = officesWithAcquiredBooks.find(o => o.officeId === selectedOfficeId);
    if (!officeData) return [];

    // Filter templates to only those that are acquired and in this office
    return templates.filter(t =>
      t.isAcquiredLoanBook &&
      officeData.acquiredTemplateIds.includes(t.id)
    );
  }, [selectedOfficeId, officesWithAcquiredBooks, templates]);

  // Auto-select first office on mount or when offices load
  useEffect(() => {
    if (!selectedOfficeId && officesWithAcquiredBooks.length > 0 && !loadingOffices) {
      const firstOffice = officesWithAcquiredBooks[0];
      updateUrlParams(firstOffice.officeId, '');
    }
  }, [officesWithAcquiredBooks, loadingOffices, selectedOfficeId]);

  // Auto-select first template when office changes or templates load
  // Skip if user has already manually clicked a template tab (prevents race condition)
  useEffect(() => {
    if (
      !userSelectedTemplateRef.current &&
      selectedOfficeId &&
      acquiredTemplatesForOffice.length > 0 &&
      !selectedTemplateId
    ) {
      updateUrlParams(selectedOfficeId, acquiredTemplatesForOffice[0].id);
    }
  }, [selectedOfficeId, acquiredTemplatesForOffice, selectedTemplateId]);

  // Fetch metrics when selection changes
  useEffect(() => {
    if (selectedTemplateId && companyId && primaryTemplate && selectedOfficeId) {
      fetchMetrics({
        companyId,
        acquiredTemplateId: selectedTemplateId,
        primaryTemplateId: primaryTemplate.id,
        officeId: selectedOfficeId,
        entityCode: resolvedParams.entityCode,
      });
    } else {
      clearMetrics();
    }
  }, [selectedTemplateId, companyId, primaryTemplate, selectedOfficeId, resolvedParams.entityCode, fetchMetrics, clearMetrics]);

  // Handle office tab click
  const handleOfficeSelect = (officeId: string) => {
    userSelectedTemplateRef.current = false; // Reset — new office needs auto-select
    updateUrlParams(officeId, '');
    clearMetrics();
  };

  // Handle template tab click
  const handleTemplateSelect = (templateId: string) => {
    userSelectedTemplateRef.current = true;
    updateUrlParams(selectedOfficeId, templateId);
  };

  // Handle refresh
  const handleRefresh = () => {
    if (selectedTemplateId && primaryTemplate && selectedOfficeId) {
      fetchMetrics({
        companyId,
        acquiredTemplateId: selectedTemplateId,
        primaryTemplateId: primaryTemplate.id,
        officeId: selectedOfficeId,
        entityCode: resolvedParams.entityCode,
      });
    }
  };

  // Get office name for display
  const getOfficeName = (officeId: string): string => {
    const office = userVisibleOffices?.find(o => o.id === officeId);
    if (office) return office.name;
    const mappedOffice = officesWithAcquiredBooks.find(o => o.officeId === officeId);
    return mappedOffice?.officeName || officeId;
  };

  // Get template name for display
  const getTemplateName = (templateId: string): string => {
    const template = templates.find(t => t.id === templateId);
    return template?.templateName || templateId;
  };

  // Validation
  if (!context || !userData || !companyData) {
    return (
      <div className="p-8 text-center text-white">
        <p className="text-red-400">Failed to load user context. Please refresh the page.</p>
      </div>
    );
  }

  // Loading state
  const isInitialLoading = loadingOffices || loadingTemplates;

  if (isInitialLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Library}
          title="Acquired Book Performance"
          subtitle="Loading office data..."
        />
        {/* Office tab skeletons */}
        <div className="flex gap-2 border-b border-gray-700 pb-0">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} width={i === 0 ? '120px' : '100px'} height="36px" className="rounded-t-lg" />
          ))}
        </div>
        {/* Template tab skeletons */}
        <div className="flex gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} width="140px" height="32px" className="rounded-lg" />
          ))}
        </div>
        {/* Content tab skeletons (Lost Loans / Confirmed Refinanced) */}
        <div className="flex gap-2">
          <Skeleton width="100px" height="28px" className="rounded-lg" />
          <Skeleton width="140px" height="28px" className="rounded-lg" />
        </div>
        <MetricsSkeleton />
      </div>
    );
  }

  // No offices with acquired books
  if (officesWithAcquiredBooks.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Library}
          title="Acquired Book Performance"
          subtitle="Track how acquired loan books perform by office"
        />
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
          <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No Acquired Books Found</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            No offices have acquired book data yet. Configure your acquired loan books in the
            <a href="/reports/refinance-intelligence" className="text-purple-400 hover:text-purple-300 ml-1">
              Client Retention
            </a> page to get started.
          </p>
        </div>
      </div>
    );
  }

  // No primary template configured
  if (!primaryTemplate) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Library}
          title="Acquired Book Performance"
          subtitle="Track how acquired loan books perform by office"
        />
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <div className="flex items-center gap-3 text-yellow-400">
            <AlertTriangle className="w-5 h-5" />
            <p>Please configure a primary loan book in the
              <a href="/reports/refinance-intelligence" className="text-purple-400 hover:text-purple-300 ml-1">
                Client Retention
              </a> page to track acquired book performance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Library}
        title="Acquired Book Performance"
        subtitle="Track how acquired loan books perform by office"
        onRefresh={selectedTemplateId ? handleRefresh : undefined}
        isRefreshing={loadingMetrics}
      />

      {/* Office Tabs (Top Level) */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-1 overflow-x-auto pb-1">
          {officesWithAcquiredBooks.map((office) => (
            <button
              key={office.officeId}
              onClick={() => handleOfficeSelect(office.officeId)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                selectedOfficeId === office.officeId
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {office.officeName}
              <span className="ml-2 text-xs opacity-70">
                ({office.acquiredTemplateIds.length})
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Acquired Template Tabs (Second Level) */}
      {selectedOfficeId && acquiredTemplatesForOffice.length > 0 && (
        <div className="border-b border-gray-700 pb-1">
          <nav className="flex space-x-4 overflow-x-auto">
            {acquiredTemplatesForOffice.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template.id)}
                className={`pb-2 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedTemplateId === template.id
                    ? 'text-cyan-400 border-cyan-400'
                    : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
                }`}
              >
                {template.templateName}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Primary Template Info */}
      {selectedTemplateId && (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <span>Comparing to:</span>
          <span className="text-green-400 font-medium">{primaryTemplate.templateName}</span>
          <span>(Primary)</span>
        </div>
      )}

      {/* Content Tabs - Lost Loans and Confirmed Refinanced */}
      {selectedTemplateId && (
        <div className="border-b border-gray-700">
          <nav className="flex space-x-1">
            <button
              onClick={() => handleTabChange('lost')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                contentTab === 'lost'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <XCircle className="w-4 h-4" />
              Lost Loans
              {lostLoansMetrics && lostLoansMetrics.total > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded bg-red-900/50">
                  {lostLoansMetrics.total}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('confirmed')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                contentTab === 'confirmed'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <CheckCircle className="w-4 h-4" />
              Confirmed Refinanced
              {metrics && metrics.retainedConfirmed > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded bg-green-900/50">
                  {metrics.retainedConfirmed}
                </span>
              )}
            </button>
          </nav>
        </div>
      )}

      {/* Error State */}
      {(officeError || metricsError) && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-red-300">
          <p>Failed to load data: {officeError?.message || metricsError?.message}</p>
        </div>
      )}

      {/* No Template Selected State */}
      {selectedOfficeId && !selectedTemplateId && acquiredTemplatesForOffice.length === 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">
            No acquired books found for {getOfficeName(selectedOfficeId)}.
          </p>
        </div>
      )}

      {/* Loading Metrics */}
      {loadingMetrics && <MetricsSkeleton />}

      {/* Metrics Display */}
      {metrics && !loadingMetrics && (
        <div className="space-y-6">
          {/* Summary Cards - Row 1: Counts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Discharged"
              value={metrics.totalDischargesFromBook.toLocaleString()}
              subValue="loans from acquired book"
              icon={<TrendingDown className="w-4 h-4 text-gray-400" />}
              color="bg-gray-700"
            />
            <MetricCard
              title="Retained to Primary"
              value={`${metrics.retainedConfirmed} `}
              subValue={metrics.retainedPending > 0 ? `+${metrics.retainedPending} pending verification` : 'all verified'}
              icon={<TrendingUp className="w-4 h-4 text-green-400" />}
              color="bg-green-900/30"
              trend="up"
            />
            <MetricCard
              title="Lost to External"
              value={metrics.lostToExternal.toLocaleString()}
              subValue="loans not retained"
              icon={<AlertTriangle className="w-4 h-4 text-red-400" />}
              color="bg-red-900/30"
              trend="down"
            />
            <MetricCard
              title="Retention Rate"
              value={`${metrics.retentionRate}%`}
              subValue={`${metrics.matchedDischargedLoans || 0} of ${metrics.totalDischargesFromBook} discharges refinanced → ${metrics.retainedToPrimary} new loans`}
              icon={<TrendingUp className="w-4 h-4 text-blue-400" />}
              color="bg-blue-900/30"
              trend={metrics.retentionRate >= 50 ? 'up' : metrics.retentionRate >= 25 ? 'neutral' : 'down'}
            />
          </div>

          {/* Summary Cards - Row 2: Loan Value Metrics + Trail Book Health */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Discharged Value"
              value={formatCurrency(metrics.dischargedValueCents)}
              subValue="loan value at discharge"
              icon={<DollarSign className="w-4 h-4 text-gray-400" />}
              color="bg-gray-700"
            />
            <MetricCard
              title="Total Refinanced Value"
              value={formatCurrency(metrics.retainedLoanAmountCents)}
              subValue="new loan values in primary book"
              icon={<DollarSign className="w-4 h-4 text-green-400" />}
              color="bg-green-900/30"
              trend="up"
            />
            {(() => {
              const loanValueGain = metrics.retainedLoanAmountCents - metrics.dischargedValueCents;
              const percentChange = metrics.dischargedValueCents > 0
                ? ((loanValueGain / metrics.dischargedValueCents) * 100).toFixed(1)
                : 0;
              const isPositive = loanValueGain >= 0;
              return (
                <MetricCard
                  title="Loan Value Gain"
                  value={`${isPositive ? '+' : ''}${formatCurrency(loanValueGain)}`}
                  subValue={`${isPositive ? '+' : ''}${percentChange}% vs discharged`}
                  icon={isPositive ? <ArrowUpRight className="w-4 h-4 text-green-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                  color={isPositive ? 'bg-green-900/30' : 'bg-red-900/30'}
                  trend={isPositive ? 'up' : 'down'}
                />
              );
            })()}
            {/* Trail Book Health: Rolling 3-run comparison for accurate trend analysis */}
            {metrics.firstRunMonth && metrics.latestRunMonth && metrics.firstRunMonth !== metrics.latestRunMonth ? (
              (() => {
                const trailChange = metrics.trailChangePercent;
                const isPositive = trailChange > 0;
                const isNeutral = trailChange === 0;
                const isNegative = trailChange < 0;
                const firstAmount = formatCurrencyCompact(metrics.firstRunTrailCents);
                const latestAmount = formatCurrencyCompact(metrics.latestRunTrailCents);
                return (
                  <MetricCard
                    title="Trail Book Change (3-run)"
                    value={`${isPositive ? '+' : ''}${trailChange}%`}
                    subValue={`${metrics.firstRunMonth} ${firstAmount} → ${metrics.latestRunMonth} ${latestAmount}`}
                    icon={
                      isNeutral
                        ? <Minus className="w-4 h-4 text-gray-400" />
                        : isNegative
                          ? <TrendingDown className="w-4 h-4 text-red-400" />
                          : <TrendingUp className="w-4 h-4 text-green-400" />
                    }
                    color={isNeutral ? 'bg-gray-700' : isNegative ? 'bg-red-900/30' : 'bg-green-900/30'}
                    trend={isNeutral ? 'neutral' : isNegative ? 'down' : 'up'}
                  />
                );
              })()
            ) : (
              <MetricCard
                title="Trail Book Change (3-run)"
                value="N/A"
                subValue="Not enough data yet"
                icon={<Minus className="w-4 h-4 text-gray-400" />}
                color="bg-gray-700"
              />
            )}
          </div>

          {/* Summary Cards - Row 3: Commission + Trail Comparison Metrics */}
          {(metrics.previousAvgMonthlyTrailCents > 0 || metrics.newAvgMonthlyTrailCents > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Total Upfront Commission"
                value={formatCurrency(metrics.retainedCommissionCents)}
                subValue={`from ${metrics.retainedToPrimary} retained loans`}
                icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                color="bg-green-900/30"
                trend="up"
              />
              <MetricCard
                title="Previous Avg Monthly Trail"
                value={formatCurrency(metrics.previousAvgMonthlyTrailCents)}
                subValue="monthly trail before discharge"
                icon={<DollarSign className="w-4 h-4 text-purple-400" />}
                color="bg-purple-900/30"
              />
              <MetricCard
                title="New Avg Monthly Trail"
                value={formatCurrency(metrics.newAvgMonthlyTrailCents)}
                subValue="monthly trail after refinance"
                icon={<DollarSign className="w-4 h-4 text-cyan-400" />}
                color="bg-cyan-900/30"
              />
              {(() => {
                const trailDelta = metrics.trailDeltaCents;
                const isPositive = trailDelta >= 0;
                const isNeutral = trailDelta === 0;
                return (
                  <MetricCard
                    title="Net Trail Impact"
                    value={`${isPositive && !isNeutral ? '+' : ''}${formatCurrency(trailDelta)}`}
                    subValue={isNeutral ? 'no change' : `${isPositive ? 'gained' : 'reduced'} per month`}
                    icon={
                      isNeutral
                        ? <Minus className="w-4 h-4 text-gray-400" />
                        : isPositive
                          ? <ArrowUpRight className="w-4 h-4 text-green-400" />
                          : <ArrowDownRight className="w-4 h-4 text-red-400" />
                    }
                    color={isNeutral ? 'bg-gray-700' : isPositive ? 'bg-green-900/30' : 'bg-red-900/30'}
                    trend={isNeutral ? 'neutral' : isPositive ? 'up' : 'down'}
                  />
                );
              })()}
            </div>
          )}

          {/* Fallback: Show commission card alone if no trail data */}
          {!(metrics.previousAvgMonthlyTrailCents > 0 || metrics.newAvgMonthlyTrailCents > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <MetricCard
                title="Total Upfront Commission"
                value={formatCurrency(metrics.retainedCommissionCents)}
                subValue={`from ${metrics.retainedToPrimary} retained loans`}
                icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                color="bg-green-900/30"
                trend="up"
              />
            </div>
          )}

          {/* Book Flow Visualization */}
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Loan Book Flow</h3>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="bg-purple-900/30 border border-purple-700 rounded-lg px-4 py-3 text-center">
                <p className="text-purple-300 text-sm">{metrics.acquiredTemplateName}</p>
                <p className="text-white font-bold">{metrics.totalDischargesFromBook} discharged</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-green-400 text-sm">{metrics.retentionRate}% retained</div>
                <div className="w-20 h-0.5 bg-gradient-to-r from-purple-500 to-green-500" />
                <div className="text-gray-400 text-sm">{100 - metrics.retentionRate}% lost</div>
              </div>
              <div className="bg-green-900/30 border border-green-700 rounded-lg px-4 py-3 text-center">
                <p className="text-green-300 text-sm">{metrics.primaryTemplateName}</p>
                <p className="text-white font-bold">{metrics.retainedConfirmed} retained</p>
                {metrics.retainedPending > 0 && (
                  <p className="text-yellow-400 text-xs">+{metrics.retainedPending} pending</p>
                )}
              </div>
            </div>
          </div>

          {/* Monthly Trend Chart */}
          {metrics.monthlyTrends.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">Monthly Retention Trend</h3>
                <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1">
                  <button
                    onClick={() => setChartView('combined')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                      chartView === 'combined'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                    title="Bar chart with retention rate line"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Detailed
                  </button>
                  <button
                    onClick={() => setChartView('simple')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                      chartView === 'simple'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                    title="Stacked area chart"
                  >
                    <LineChart className="w-4 h-4" />
                    Stacked
                  </button>
                </div>
              </div>
              <RetentionTrendChart
                data={metrics.monthlyTrends}
                title=""
                viewMode={chartView}
              />
            </div>
          )}

          {/* Yearly Summary Table */}
          {metrics.yearlySummary.length > 0 && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <h3 className="text-lg font-medium text-white px-4 py-3 border-b border-gray-700">
                Yearly Summary
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900">
                      <th className="px-4 py-3 text-left text-gray-400 font-medium">Year</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Discharged</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Retained (Verified/Pending)</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Lost</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Rate</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Upfront Commission</th>
                      <th className="px-4 py-3 text-right text-gray-400 font-medium">Loan Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.yearlySummary.map((year) => (
                      <tr key={year.year} className="border-t border-gray-700 hover:bg-gray-750">
                        <td className="px-4 py-3 text-white font-medium">{year.year}</td>
                        <td className="px-4 py-3 text-right text-gray-300">{year.discharges.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-green-400">{year.retainedConfirmed}</span>
                          {year.retainedPending > 0 && (
                            <span className="text-yellow-400 ml-1">+{year.retainedPending}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-red-400">{year.lost.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            year.retentionRate >= 50 ? 'bg-green-900/50 text-green-300' :
                            year.retentionRate >= 25 ? 'bg-yellow-900/50 text-yellow-300' :
                            'bg-red-900/50 text-red-300'
                          }`}>
                            {year.retentionRate}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-green-400">
                          {formatCurrency(year.retainedCommissionCents)}
                        </td>
                        <td className="px-4 py-3 text-right text-cyan-400">
                          {formatCurrency(year.retainedLoanAmountCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {(() => {
                      // Use pre-computed metrics totals instead of summing yearly data
                      // Yearly sums can double-count loans that appear in multiple years
                      const totals = {
                        discharges: metrics.totalDischargesFromBook,
                        retainedConfirmed: metrics.retainedConfirmed,
                        retainedPending: metrics.retainedPending,
                        lost: metrics.lostToExternal,
                        commissionCents: metrics.retainedCommissionCents,
                        loanValueCents: metrics.retainedLoanAmountCents,
                      };
                      const totalRetained = totals.retainedConfirmed + totals.retainedPending;
                      const overallRate = metrics.retentionRate;
                      return (
                        <tr className="border-t-2 border-gray-600 bg-gray-900 font-semibold">
                          <td className="px-4 py-3 text-white">Total</td>
                          <td className="px-4 py-3 text-right text-gray-200">{totals.discharges.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-green-400">{totals.retainedConfirmed}</span>
                            {totals.retainedPending > 0 && (
                              <span className="text-yellow-400 ml-1">+{totals.retainedPending}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-red-400">{totals.lost.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              overallRate >= 50 ? 'bg-green-900/50 text-green-300' :
                              overallRate >= 25 ? 'bg-yellow-900/50 text-yellow-300' :
                              'bg-red-900/50 text-red-300'
                            }`}>
                              {overallRate}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-green-400">
                            {formatCurrency(totals.commissionCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-cyan-400">
                            {formatCurrency(totals.loanValueCents)}
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Empty state for no data */}
          {metrics.totalDischargesFromBook === 0 && (
            <div className="text-center py-8 text-gray-400 bg-gray-800 rounded-lg border border-gray-700">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No discharge data found for this acquired book.</p>
              <p className="text-sm mt-1">
                This could mean no loans have been discharged yet, or the data has not been uploaded.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ===== LOST LOANS TAB CONTENT ===== */}
      {selectedTemplateId && contentTab === 'lost' && (
        <div id="tab-content-section" className="space-y-6">
          {/* Loss Reason Stat Cards */}
          {lostLoansMetrics && !loadingLostLoans && (
            <LossReasonStatCards
              metrics={lostLoansMetrics}
              selectedReason={lossReasonFilter}
              onReasonSelect={(reason: LossReason | 'all') => setLossReasonFilter(reason)}
            />
          )}

          {/* Filters */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center flex-wrap gap-4">
              {/* Categorization Status Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-400">Status:</label>
                <select
                  value={categorizationFilter}
                  onChange={(e) => setCategorizationFilter(e.target.value as 'all' | 'needs_categorization' | 'completed')}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="needs_categorization">Needs Categorization</option>
                  <option value="completed">Completed</option>
                  <option value="all">All</option>
                </select>
              </div>

              {/* Broker Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-400">Broker:</label>
                <select
                  value={brokerFilter}
                  onChange={(e) => setBrokerFilter(e.target.value)}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Brokers</option>
                  {brokerList.map(broker => (
                    <option key={broker.id} value={broker.id}>{broker.name}</option>
                  ))}
                </select>
              </div>

              {/* Loss Reason Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-400">Reason:</label>
                <select
                  value={lossReasonFilter}
                  onChange={(e) => setLossReasonFilter(e.target.value as LossReason | 'all')}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Reasons</option>
                  {/* Filter list derives from the canonical taxonomy so a new
                      reason (e.g. uncontactable) is filterable automatically. */}
                  {ALL_LOSS_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {LOSS_REASON_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {selectedLoanIds.size > 0 && (
              <div className="text-sm text-blue-400">
                {selectedLoanIds.size} loan{selectedLoanIds.size > 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {/* Loading State */}
          {loadingLostLoans && (
            <div className="flex items-center justify-center h-32">
              <Spinner size="md" label="Loading lost loans..." />
            </div>
          )}

          {/* Error State */}
          {lostLoansError && (
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-red-300">
              <p>Failed to load lost loans: {lostLoansError.message}</p>
            </div>
          )}

          {/* Empty State */}
          {!loadingLostLoans && !lostLoansError && lostLoans.length === 0 && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
              {categorizationFilter === 'needs_categorization' ? (
                <>
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-300 mb-2">All Loans Categorized</h3>
                  <p className="text-gray-500 text-sm">
                    No more loans need categorization. Switch to "Completed" to view categorized loans.
                  </p>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-300 mb-2">No Lost Loans Found</h3>
                  <p className="text-gray-500 text-sm">
                    {lossReasonFilter === 'all'
                      ? 'No discharged loans found matching the current filters.'
                      : `No loans found with reason: ${lossReasonFilter.replace(/_/g, ' ')}`}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Lost Loans List */}
          {!loadingLostLoans && lostLoans.length > 0 && (
            <>
              <div className="space-y-3">
                {lostLoans.map((loan) => {
                  const loanEntries = contactsByLoan[loan.loanId] || [];
                  const isHighlighted = highlightedLoanId === loan.loanId;
                  return (
                    <div
                      key={loan.loanId}
                      ref={isHighlighted ? deepLinkCardRef : undefined}
                      className={isHighlighted ? 'ring-2 ring-blue-500/50 rounded-lg' : ''}
                    >
                    <LostLoanCard
                      loan={loan}
                      isSelected={selectedLoanIds.has(loan.loanId)}
                      onToggleSelect={() => toggleLoanSelection(loan.loanId)}
                      onSetReason={async (loanId, reason, notes) => {
                        return handleSetLossReason(loanId, reason, notes);
                      }}
                      isSaving={savingLossReason}
                      isContactExpanded={expandedCards.has(loan.loanId)}
                      onToggleContactExpansion={() => toggleCardExpansion(loan.loanId)}
                      contactCount={loanEntries.length}
                      contactSlot={
                        <ContactTimeline
                          companyId={companyId}
                          loanId={loan.loanId}
                          customerName={loan.customerName}
                          loanBroker={loan.brokerDisplay || undefined}
                          sourcePage="Acquired Books"
                          entries={loanEntries}
                          availableBrokers={contactBrokerList}
                          currentUser={{
                            uid: userData?.uid,
                            displayName: userData?.commissionDataName || userData?.displayName,
                            email: userData?.email,
                            role: userData?.role,
                          }}
                          onMutate={mutateContact}
                        />
                      }
                    />
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={lostLoansPage}
                totalItems={lostLoansTotal}
                pageSize={lostLoansPageSize}
                onPageChange={setLostLoansPage}
                onPageSizeChange={(size) => {
                  setLostLoansPageSize(size);
                  setLostLoansPage(1);
                }}
              />
            </>
          )}

          {/* Bulk Action Bar */}
          {selectedLoanIds.size > 0 && (
            <BulkActionBar
              selectedCount={selectedLoanIds.size}
              onSetReason={handleBulkSetReason}
              onClearSelection={clearSelection}
              isSaving={savingLossReason}
            />
          )}
        </div>
      )}

      {/* ===== CONFIRMED REFINANCED TAB CONTENT ===== */}
      {selectedTemplateId && contentTab === 'confirmed' && (
        <div id="tab-content-section" className="space-y-6">
          {/* Confirmed Refinance Summary */}
          {metrics && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 border border-green-500/30">
                <p className="text-sm text-gray-400 mb-1">Confirmed Refinances</p>
                <p className="text-2xl font-bold text-green-400">{metrics.retainedConfirmed}</p>
                <p className="text-xs text-gray-500 mt-1">verified matches</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-sm text-gray-400 mb-1">Commission Earned</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(metrics.retainedConfirmedCommissionCents)}</p>
                <p className="text-xs text-gray-500 mt-1">from confirmed refinances</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-sm text-gray-400 mb-1">Total Loan Value</p>
                <p className="text-2xl font-bold text-cyan-400">{formatCurrency(metrics.retainedLoanAmountCents)}</p>
                <p className="text-xs text-gray-500 mt-1">refinanced to primary</p>
              </div>
            </div>
          )}

          {/* Confirmed Refinances List */}
          {loadingRefinanceMatches ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="md" label="Loading confirmed refinances..." />
            </div>
          ) : refinanceMatches.length === 0 ? (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
              <CheckCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-300 mb-2">No Confirmed Refinances</h3>
              <p className="text-gray-500 text-sm">
                No confirmed acquired-to-primary refinances found for this book.
              </p>
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-400 mb-3">
                Showing {uniqueRefinanceCount} unique refinances
                {groupedRefinances.groups.size > 0 && (
                  <span className="text-gray-500 ml-1">
                    ({groupedRefinances.groups.size} split loan groups, {groupedRefinances.singles.length} individual)
                  </span>
                )}
              </div>

              {/* Confirmed Refinance Cards - Grouped by splitGroupId */}
              <div className="space-y-3">
                {/* Render split loan groups first */}
                {Array.from(groupedRefinances.groups.entries()).map(([groupId, groupMatches]) => {
                  // Use first match for main display, aggregate totals
                  const firstMatch = groupMatches[0];
                  const totalCommission = groupMatches.reduce((sum, m) => sum + (m.newLoan.commissionCents || 0), 0);
                  const totalLoanAmount = groupMatches.reduce((sum, m) => sum + (m.newLoan.loanAmountCents || 0), 0);
                  const totalDischarged = groupMatches.reduce((sum, m) => sum + (m.discharged.balanceAtDischargeCents || 0), 0);

                  return (
                    <div
                      key={groupId}
                      className="bg-gray-800 rounded-lg border border-green-500/30 p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Badges */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium border border-green-500/30">
                              CONFIRMED
                            </span>
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium border border-purple-500/30">
                              {groupMatches.length} loans combined
                            </span>
                            {firstMatch.isAcquiredToPrimary && (
                              <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                Acquired to Primary
                              </span>
                            )}
                          </div>

                          {/* Customer Name */}
                          <div className="flex items-center gap-2 text-lg">
                            <span className="font-medium text-white">{firstMatch.discharged.customerName}</span>
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                            <span className="font-medium text-white">{firstMatch.newLoan.customerName}</span>
                          </div>

                          {/* Loan Details - Aggregated */}
                          <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                            <span>
                              {formatCurrency(totalDischarged)} discharged
                            </span>
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                            <span className="text-green-400 font-medium">
                              {formatCurrency(totalLoanAmount)} new loans
                            </span>
                          </div>

                          {/* Flow Details */}
                          <div className="flex items-center gap-2 mt-2 text-sm">
                            <span className="text-gray-500">{firstMatch.discharged.templateName || 'Unknown'}</span>
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                            <span className="text-cyan-400">{firstMatch.newLoan.templateName || 'Unknown'}</span>
                          </div>

                          {/* Settlement Info */}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>Settled: {new Date(firstMatch.newLoan.settlementDate).toLocaleDateString('en-AU')}</span>
                            <span className="text-green-400">{formatCurrency(totalCommission)} total commission</span>
                          </div>

                          {/* Verification Info */}
                          {firstMatch.verifiedByEmail && (
                            <div className="mt-2 text-xs text-gray-500">
                              Verified by {firstMatch.verifiedByEmail}
                              {firstMatch.verifiedAt && ` on ${new Date(firstMatch.verifiedAt).toLocaleDateString('en-AU')}`}
                            </div>
                          )}
                        </div>

                        {/* Commission Badge - Aggregated */}
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-bold text-green-400">
                            {formatCurrency(totalCommission)}
                          </p>
                          <p className="text-xs text-gray-500">total commission</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Render single (non-split) matches */}
                {groupedRefinances.singles.map((match) => (
                  <div
                    key={match.matchId}
                    className="bg-gray-800 rounded-lg border border-green-500/30 p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Badges */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium border border-green-500/30">
                            CONFIRMED
                          </span>
                          {match.isAcquiredToPrimary && (
                            <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              Acquired to Primary
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            match.confidenceScore >= 80
                              ? 'bg-green-500/10 text-green-500'
                              : match.confidenceScore >= 50
                              ? 'bg-yellow-500/10 text-yellow-500'
                              : 'bg-red-500/10 text-red-500'
                          }`}>
                            {match.confidenceScore}% confidence
                          </span>
                        </div>

                        {/* Customer Name */}
                        <div className="flex items-center gap-2 text-lg">
                          <span className="font-medium text-white">{match.discharged.customerName}</span>
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                          <span className="font-medium text-white">{match.newLoan.customerName}</span>
                        </div>

                        {/* Loan Details */}
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                          <span>
                            {formatCurrency(match.discharged.balanceAtDischargeCents || 0)} discharged
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                          <span className="text-green-400 font-medium">
                            {formatCurrency(match.newLoan.loanAmountCents)} new loan
                          </span>
                        </div>

                        {/* Flow Details */}
                        <div className="flex items-center gap-2 mt-2 text-sm">
                          <span className="text-gray-500">{match.discharged.templateName || 'Unknown'}</span>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                          <span className="text-cyan-400">{match.newLoan.templateName || 'Unknown'}</span>
                        </div>

                        {/* Settlement Info */}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span>Settled: {new Date(match.newLoan.settlementDate).toLocaleDateString('en-AU')}</span>
                          <span>{match.daysBetween} days between</span>
                          <span className="text-green-400">{formatCurrency(match.newLoan.commissionCents)} commission</span>
                        </div>

                        {/* Verification Info */}
                        {match.verifiedByEmail && (
                          <div className="mt-2 text-xs text-gray-500">
                            Verified by {match.verifiedByEmail}
                            {match.verifiedAt && ` on ${new Date(match.verifiedAt).toLocaleDateString('en-AU')}`}
                            {match.outcomeType && (
                              <span className="text-green-400 ml-2">({match.outcomeType.replace(/_/g, ' ')})</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Commission Badge */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-green-400">
                          {formatCurrency(match.newLoan.commissionCents)}
                        </p>
                        <p className="text-xs text-gray-500">commission</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More */}
              {hasMoreRefinanceMatches && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={loadMoreRefinanceMatches}
                    disabled={loadingRefinanceMatches}
                    className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium border border-gray-600 flex items-center gap-2 transition-all disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                    Load More
                  </button>
                </div>
              )}
            </>
          )}

          {/* Error state */}
          {refinanceError && (
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-red-300">
              <p>Failed to load refinance matches: {refinanceError.message}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AcquiredBooksPage;
